/* ==========================================================================
   SASH STORE — "MIDNIGHT" motion layer
   Hero canvas, scroll reveal, card tilt, custom cursor, sticky header.
   Eligibility comes from window.SashGate (assets/sash-gate.js) and is
   re-checked on resize, so widening a window turns the motion on.
   ========================================================================== */
(function () {
  'use strict';

  var G = window.SashGate || {
    reduced: window.matchMedia('(prefers-reduced-motion: reduce)'),
    fine: window.matchMedia('(pointer: fine)'),
    motionOK: function () { return !window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  };

  var HERO_MIN_WIDTH = 600;

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 1600 });
    else setTimeout(fn, 400);
  }

  /* ----------------------------------------------------------------------
     1. Hero particle field. Returns true once running, false for "retry".
     ---------------------------------------------------------------------- */
  function initHero() {
    var canvas = document.querySelector('.sash-hero__canvas');
    if (!canvas) return true;
    if (canvas.dataset.sashLive) return true;
    if (!G.motionOK()) return true;
    if (window.innerWidth < HERO_MIN_WIDTH) return false;

    var ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return true;
    canvas.dataset.sashLive = '1';

    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = 0, h = 0, particles = [], raf = null, visible = true;
    var pointer = { x: -9999, y: -9999 };

    function size() {
      var rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      if (!w || !h) return;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var target = Math.min(Math.round((w * h) / 12000), 130);
      particles = [];
      for (var i = 0; i < target; i++) {
        particles.push({
          x: Math.random() * w, y: Math.random() * h,
          r: Math.random() * 1.6 + 0.35,
          vx: (Math.random() - 0.5) * 0.16,
          vy: -(Math.random() * 0.26 + 0.04),
          a: Math.random() * 0.5 + 0.12,
          tw: Math.random() * Math.PI * 2
        });
      }
    }

    function frame() {
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx; p.y += p.vy; p.tw += 0.015;

        var dx = p.x - pointer.x, dy = p.y - pointer.y;
        var d2 = dx * dx + dy * dy;
        if (d2 < 26000) {
          var f = (1 - d2 / 26000) * 0.55;
          p.x += (dx / (Math.sqrt(d2) || 1)) * f;
          p.y += (dy / (Math.sqrt(d2) || 1)) * f;
        }

        if (p.y < -12) { p.y = h + 12; p.x = Math.random() * w; }
        if (p.x < -12) p.x = w + 12;
        if (p.x > w + 12) p.x = -12;

        var alpha = p.a * (0.6 + 0.4 * Math.sin(p.tw));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(190, 172, 255, ' + alpha.toFixed(3) + ')';
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    }

    function start() { if (!raf && visible && !document.hidden) raf = requestAnimationFrame(frame); }
    function stop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

    size();
    canvas.classList.add('is-live');
    start();

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) {
        visible = e[0].isIntersecting;
        visible ? start() : stop();
      }, { threshold: 0 }).observe(canvas);
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t); t = setTimeout(size, 200);
    }, { passive: true });

    canvas.parentElement.addEventListener('pointermove', function (e) {
      var r = canvas.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
    }, { passive: true });

    canvas.parentElement.addEventListener('pointerleave', function () {
      pointer.x = -9999; pointer.y = -9999;
    }, { passive: true });

    return true;
  }

  /* ----------------------------------------------------------------------
     2. Scroll reveal
     ---------------------------------------------------------------------- */
  function initReveal() {
    if (!('IntersectionObserver' in window)) return;

    var blocks = document.querySelectorAll(
      '.shopify-section:not(.section-header):not(.sash-hero-section) .title-wrapper-with-link,' +
      '.shopify-section .card-wrapper,' +
      '.shopify-section .collection-list__item,' +
      '.shopify-section .rich-text__wrapper,' +
      '.shopify-section .multicolumn-card,' +
      '.shopify-section .banner__content'
    );
    if (!blocks.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    blocks.forEach(function (el, i) {
      if (el.classList.contains('sash-reveal')) return;
      if (G.reduced.matches) { el.classList.add('is-in'); return; }
      el.classList.add('sash-reveal');
      el.setAttribute('data-sash-delay', String(i % 5));
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     3. Card tilt + rim light (fine pointers only)
     ---------------------------------------------------------------------- */
  function initTilt() {
    if (!G.fine.matches || !G.motionOK()) return;
    if (document.body.dataset.sashTilt) return;
    document.body.dataset.sashTilt = '1';

    var pending = false, queue = [];

    function flush() {
      pending = false;
      queue.forEach(function (job) { job(); });
      queue = [];
    }

    document.addEventListener('pointermove', function (e) {
      var wrapper = e.target.closest && e.target.closest('.card-wrapper');
      if (!wrapper) return;
      var card = wrapper.querySelector('.card');
      if (!card) return;
      if (wrapper.closest('[data-sash-depth="3d"]')) return;  // the corridor owns that transform

      queue.push(function () {
        var r = wrapper.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        if (px < 0 || px > 1 || py < 0 || py > 1) return;
        card.style.setProperty('--sash-mx', (px * 100).toFixed(1) + '%');
        card.style.setProperty('--sash-my', (py * 100).toFixed(1) + '%');
        card.style.transform =
          'perspective(1200px) rotateX(' + ((0.5 - py) * 5).toFixed(2) + 'deg) ' +
          'rotateY(' + ((px - 0.5) * 6).toFixed(2) + 'deg) translate3d(0,-5px,0)';
      });

      if (!pending) { pending = true; requestAnimationFrame(flush); }
    }, { passive: true });

    document.addEventListener('pointerout', function (e) {
      var wrapper = e.target.closest && e.target.closest('.card-wrapper');
      if (!wrapper || wrapper.contains(e.relatedTarget)) return;
      var card = wrapper.querySelector('.card');
      if (card) card.style.transform = '';
    }, { passive: true });
  }

  /* ----------------------------------------------------------------------
     4. Custom cursor
     ---------------------------------------------------------------------- */
  function initCursor() {
    if (!G.fine.matches || !G.motionOK()) return;
    if (document.querySelector('.sash-cursor')) return;

    var dot = document.createElement('div');
    var ring = document.createElement('div');
    dot.className = 'sash-cursor';
    ring.className = 'sash-cursor-ring';
    document.body.appendChild(dot);
    document.body.appendChild(ring);

    var mx = innerWidth / 2, my = innerHeight / 2, rx = mx, ry = my, raf = null;

    function loop() {
      rx += (mx - rx) * 0.16;
      ry += (my - ry) * 0.16;
      dot.style.transform = 'translate3d(' + (mx - 3) + 'px,' + (my - 3) + 'px,0)';
      ring.style.transform = 'translate3d(' + (rx - ring.offsetWidth / 2) + 'px,' + (ry - ring.offsetHeight / 2) + 'px,0)';
      raf = requestAnimationFrame(loop);
    }

    document.addEventListener('pointermove', function (e) {
      mx = e.clientX; my = e.clientY;
      if (!document.body.classList.contains('sash-cursor-on')) {
        document.body.classList.add('sash-cursor-on');
      }
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });

    document.addEventListener('pointerover', function (e) {
      var hot = e.target.closest && e.target.closest('a, button, .card-wrapper, .sash-panel__card, summary, [role="button"]');
      document.body.classList.toggle('sash-cursor-hot', !!hot);
    }, { passive: true });

    document.addEventListener('pointerleave', function () {
      document.body.classList.remove('sash-cursor-on');
    });
  }

  /* ----------------------------------------------------------------------
     5. Sticky header state
     ---------------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('.header-wrapper');
    if (!header || header.dataset.sashHeader) return;
    header.dataset.sashHeader = '1';
    var ticking = false;

    function update() {
      ticking = false;
      header.classList.toggle('sash-stuck', window.scrollY > 24);
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });

    update();
  }

  /* ---------------------------------------------------------------------- */
  var heroDone = false;

  function boot() {
    initHeader();
    initReveal();
    initTilt();
    initCursor();
    idle(function () { heroDone = initHero(); });
  }

  var rt;
  window.addEventListener('resize', function () {
    if (heroDone) return;
    clearTimeout(rt);
    rt = setTimeout(function () { heroDone = initHero(); }, 250);
  }, { passive: true });

  if (G.reduced.addEventListener) {
    G.reduced.addEventListener('change', function () { heroDone = initHero(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    initReveal();
    heroDone = initHero();
  });
})();
